<script setup>
import { ref, reactive, computed, onMounted, onActivated, watch } from 'vue'
import api from '@/services/api'
import PaginaAdministrar from '@/components/base/grid/PaginaAdministrar.vue'
import AdminTable from '@/components/base/grid/AdminTable.vue'
import AuditLogViewer from '@/components/base/auditoria/AuditLogViewer.vue'
import { useAuthStore } from '@/stores/auth'
import { usePaginacao } from '@/composables/usePaginacao'
import { useUI } from '@/composables/useUI'

const emit = defineEmits(['adicionar-item', 'editar-item', 'excluir-item'])

const authStore = useAuthStore()
const { obterClasseStatusGhost } = useUI()
const { paginacao, injetarParametros, atualizarPaginacao } = usePaginacao()

const planos = ref([])
const loading = ref(false)
const erro = ref(null)
const filtrosAbertos = ref(false)

const sortField = ref('')
const sortOrder = ref('')

const listas = reactive({
  statusPlanos: []
})

const filtros = reactive({
  busca: '',
  lstStatusPlano: ''
})

const mostrarAuditoria = ref(false)
const auditoriaSelecionada = reactive({ id: null, nome: '' })

const temPermissaoCompleta = computed(() => authStore.pode('CATEGORIAS_FINANCEIRAS_COMPLETO'))
const temPermissaoVisualizacao = computed(() => authStore.pode('CATEGORIAS_FINANCEIRAS_VISUALIZACAO'))
const podeVerAcoes = computed(() => temPermissaoCompleta.value || temPermissaoVisualizacao.value)

const colunas = computed(() => {
  const cols = [
    { field: 'idPlanoCategoriaFinanceira', label: 'ID', width: 100, sortable: true, slotName: 'id' },
    { field: 'nome', label: 'Nome do Plano', sortable: true, class: 'fw-medium', slotName: 'nome' },
    { field: 'mascaraFormatacao', label: 'Máscara', width: 120, align: 'center', sortable: true, slotName: 'mascara' },
    { field: 'totalCategorias', label: 'Categorias', width: 100, align: 'center', sortable: true, slotName: 'totalCategorias' },
    { field: 'lstStatusPlano', label: 'Status', width: 150, align: 'center', sortable: true, slotName: 'status' }
  ]
  if (podeVerAcoes.value) {
    cols.push({ field: 'acoes', label: 'Ações', width: 150, align: 'right', slotName: 'acoes', resizable: false, sortable: false })
  }
  return cols
})

const sortResolver = (field, order, data) => {
  if (!field.startsWith('lst')) return null

  const resolvers = {
    lstStatusPlano: (row) => getStatusTexto(row.lstStatusPlano)
  }

  const resolver = resolvers[field]
  if (!resolver) return null

  return [...data].sort((a, b) => {
    const cmp = resolver(a).localeCompare(resolver(b), 'pt-BR')
    return order === 'asc' ? cmp : -cmp
  })
}

const handleSortChange = () => {
  buscarDados(1)
}

watch(() => authStore.empresaAtiva, (novaEmpresa) => {
  if (novaEmpresa) {
    buscarDados(1)
  } else {
    planos.value = []
    paginacao.value.TotalCount = 0
  }
})

const carregarListas = async () => {
  try {
    const res = await api.get('/ListasSistema/lookup/FIN_ST_PLAN_CATEGFIN')
    listas.statusPlanos = res.data
  } catch (e) {
    console.warn('Erro ao carregar listas:', e)
  }
}

const getStatusTexto = (idStatus) => {
  if (!idStatus) return '-'
  const obj = listas.statusPlanos.find(s => s.idListaSistemaItem === idStatus || s.idListaClienteItem === idStatus)
  return obj ? obj.textoLista : idStatus
}

const buscarDados = async (pageNumber = 1) => {
  if (authStore.isAdminInterno && !authStore.empresaAtiva) {
    erro.value = 'Por favor, selecione uma Empresa no topo da página.'
    planos.value = []
    return
  }

  loading.value = true
  erro.value = null

  try {
    const params = injetarParametros(pageNumber)

    if (authStore.empresaAtiva) params.append('idEmpresa', authStore.empresaAtiva)
    if (filtros.busca) params.append('busca', filtros.busca)
    if (filtros.lstStatusPlano) params.append('lstStatusPlano', filtros.lstStatusPlano)
    if (sortField.value) params.append('sortField', sortField.value)
    if (sortOrder.value) params.append('sortOrder', sortOrder.value)

    const response = await api.get(`/PlanosCategoriasFinanceiras?${params.toString()}`)
    planos.value = response.data
    atualizarPaginacao(response.headers)
  } catch (error) {
    console.error('Erro ao buscar planos de categorias:', error)
    erro.value = 'Não foi possível carregar os dados dos planos.'
    planos.value = []
    paginacao.value.TotalCount = 0
  } finally {
    loading.value = false
  }
}

const aplicarFiltros = () => { buscarDados(1) }
const limparFiltros = () => { filtros.busca = ''; filtros.lstStatusPlano = ''; buscarDados(1) }

const mudarPagina = (page) => {
  if (page >= 1 && page <= (paginacao.value.TotalPages || 1)) buscarDados(page)
}

const mudarTamanhoPagina = (novoTamanho) => {
  paginacao.value.PageSize = novoTamanho
  buscarDados(1)
}

const adicionarPlano = () => { emit('adicionar-item') }
const editarPlano = (plano) => { emit('editar-item', plano) }

const abrirAuditoria = (plano) => {
  auditoriaSelecionada.id = plano.idPlanoCategoriaFinanceira
  auditoriaSelecionada.nome = plano.nome
  mostrarAuditoria.value = true
}

onMounted(async () => {
  await carregarListas()
  if (authStore.empresaAtiva || !authStore.isAdminInterno) buscarDados(1)
})

onActivated(() => { buscarDados(paginacao.value.CurrentPage || 1) })

defineExpose({ buscarDados })
</script>

<template>
  <PaginaAdministrar
    titulo="Planos de Categorias Financeiras"
    subtitulo="Gestão dos planos de classificação de receitas e despesas"
    v-model:filtros-abertos="filtrosAbertos"
    :total-itens="paginacao.TotalCount"
    :itens-por-pagina="paginacao.PageSize"
    :pagina-atual="paginacao.CurrentPage"
    @mudar-pagina="mudarPagina"
    @mudar-tamanho-pagina="mudarTamanhoPagina"
    @adicionar="adicionarPlano"
    @atualizar="aplicarFiltros"
    :mostrar-botao-adicionar="temPermissaoCompleta"
  >
    <template #acoes="{ toggleFiltros }">
      <button class="btn btn-dark" @click="toggleFiltros">
        <i class="bi bi-filter-lg me-2"></i>
        <span>Filtros</span>
      </button>
    </template>

    <template #filtros>
      <h3 class="filtros-titulo">Filtrar por:</h3>
      <div class="row g-3 align-items-end">
        <div class="col-md-4">
          <label for="filtro-busca" class="form-label">Nome do Plano</label>
          <input type="text" id="filtro-busca" class="form-control" v-model="filtros.busca" placeholder="Digite para buscar..." @keydown.enter.prevent="aplicarFiltros">
        </div>

        <div class="col-md-3">
          <label for="filtro-status" class="form-label">Status</label>
          <select id="filtro-status" class="form-select" v-model="filtros.lstStatusPlano" @change="aplicarFiltros">
            <option value="">Todos</option>
            <option v-for="st in listas.statusPlanos" :key="st.idListaSistemaItem" :value="st.textoLista">
              {{ st.textoLista }}
            </option>
          </select>
        </div>

        <div class="col-auto">
          <button class="btn btn-secondary me-2" @click="limparFiltros">Limpar</button>
          <button class="btn btn-primary" @click="aplicarFiltros">Aplicar Filtros</button>
        </div>
      </div>
    </template>

    <div v-if="loading" class="text-center p-5">
      <div class="spinner-border text-primary" role="status">
        <span class="visually-hidden">A carregar...</span>
      </div>
    </div>

    <div v-else-if="erro" class="alert alert-danger m-3 border-0 shadow-sm">
      <i class="bi bi-info-circle-fill me-2"></i> {{ erro }}
    </div>

    <AdminTable
      v-else
      :columns="colunas"
      :data="planos"
      :loading="loading"
      empty-message="Nenhum plano de categorias financeiras encontrado."
      row-key="idPlanoCategoriaFinanceira"
      v-model:sort-field="sortField"
      v-model:sort-order="sortOrder"
      :sort-resolver="sortResolver"
      @sort-change="handleSortChange"
      @row-dblclick="editarPlano($event.row)"
    >
      <template #id="{ row }">
        <span class="text-muted fw-medium">#{{ row.idPlanoCategoriaFinanceira }}</span>
      </template>
      <template #nome="{ row }">
        <i v-if="row.isTemplateSistema" class="bi bi-cloud-fill text-body-tertiary me-1 small" title="Template do Sistema"></i>
        {{ row.nome }}
      </template>

      <template #mascara="{ row }">
        <code class="small">{{ row.mascaraFormatacao }}</code>
      </template>

      <template #totalCategorias="{ row }">
        <span class="badge bg-body-tertiary text-body border border-secondary-subtle">
          {{ row.totalCategorias }}
        </span>
      </template>

      <template #status="{ row }">
        <span :class="obterClasseStatusGhost(getStatusTexto(row.lstStatusPlano))">
          {{ getStatusTexto(row.lstStatusPlano) }}
        </span>
      </template>

      <template #acoes="{ row }">
        <div class="acoes-wrapper" v-if="podeVerAcoes">
          <button class="btn-icon" title="Ver Histórico de Alterações" @click="abrirAuditoria(row)">
            <i class="bi bi-clock-history text-secondary"></i>
          </button>
          <button class="btn-icon text-primary" title="Editar" @click="editarPlano(row)">
            <i class="bi bi-pencil-fill"></i>
          </button>
        </div>
      </template>
    </AdminTable>
  </PaginaAdministrar>

  <AuditLogViewer
    v-if="mostrarAuditoria"
    tabela="PlanoCategoriaFinanceira"
    :chave-primaria="auditoriaSelecionada.id"
    :titulo-registro="auditoriaSelecionada.nome"
    :dicionarios="{
      'lstStatusPlano': listas.statusPlanos
    }"
    @fechar="mostrarAuditoria = false"
  />
</template>

<style scoped>
.filtros-titulo {
  font-size: 1.1rem;
  font-weight: 600;
  color: #495057;
  margin-bottom: 1rem;
}
</style>
